import { destroySlider, debounce, smoothSwiperOverrides, createSlider } from '@theme/coco-utilities';
import PhotoSwipeLightbox from '@lib/photoswipe-lightbox';

if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = null;
      this.thumbnailsInstance = null;
      this._createRaf = null;
      this._layoutRaf = null;
      this._lastIsDesktop = null;
      this.slider = null;
      this.thumbnails = null;
      this.lightbox = null;
      this.galleryEl = null;
      this.mediaQuery = window.matchMedia('(min-width: 1025px)');
      this.onResize = debounce(() => this.handleSlider({ fromResize: true }), 150);
      this.onViewportResize = debounce(() => this.syncSliderSize(), 150);
    }

    /** Soft update when viewport chrome / orientation changes without a layout breakpoint flip. */
    syncSliderSize() {
      const isDesktop = this.mediaQuery.matches;
      const breakpointChanged =
        this._lastIsDesktop != null && this._lastIsDesktop !== isDesktop;

      if (breakpointChanged) {
        this.handleSlider({ fromResize: true });
        return;
      }

      if (!this.sliderInstance || this.sliderInstance.destroyed) return;

      try {
        this.sliderInstance.update();
        this.sliderInstance.updateSize?.();
        this.sliderInstance.updateSlides?.();
        this.thumbnailsInstance?.update?.();
      } catch (_) {
        this.handleSlider({ fromResize: true });
      }
    }

    getDesktopLayout() {
      return this.dataset.desktopLayout || 'vertical-carousel';
    }

    isInQuickView() {
      return Boolean(this.closest('[is="quick-view"], .quick-view'));
    }

    /** Grid / stacked layouts on desktop — CSS grid, no Swiper. */
    isDesktopGridLayout() {
      if (!this.mediaQuery.matches) return false;
      const layout = this.getDesktopLayout();
      return layout !== 'vertical-carousel' && layout !== 'scrollable';
    }

    /** Legacy static desktop gallery — replaced by Liquid data-slider Swiper. */
    isDesktopStaticGallery() {
      return false;
    }

    /** Mobile (&lt;1025): JS hardcoded carousel params (thumbs module, bullets). */
    shouldUseCarousel() {
      return !this.mediaQuery.matches;
    }

    /** Desktop vertical-carousel / scrollable: Swiper from Liquid `data-slider`. */
    shouldUseLiquidSlider() {
      return this.mediaQuery.matches && !this.isDesktopGridLayout();
    }

    shouldInitSwiper() {
      return this.shouldUseCarousel() || this.shouldUseLiquidSlider();
    }

    updateLayoutMode() {
      this.toggleAttribute('data-media-grid', this.isDesktopGridLayout());
      this.toggleAttribute('data-desktop-static', this.isDesktopStaticGallery());
    }

    refreshGalleryElements() {
      this.slider =
        this.querySelector('[data-slider]') ??
        (this.hasAttribute('data-slider') ? this : null);
      this.thumbnails =
        this.querySelector('[data-thumbnails]') ??
        (this.hasAttribute('data-thumbnails') ? this : null);
    }

    /** Thumbs wrapper may be `none md-block` — check that, not the inner swiper. */
    areThumbnailsVisible() {
      if (!this.thumbnails?.isConnected) return false;

      const wrap =
        this.thumbnails.closest('[data-product-gallery-thumbs]') || this.thumbnails;

      const style = window.getComputedStyle(wrap);
      return style.display !== 'none' && style.visibility !== 'hidden';
    }

    isQuickViewReady() {
      const quickView = this.closest('[is="quick-view"], .quick-view');
      if (!quickView) return true;
      return (
        quickView.hasAttribute('active') || quickView.hasAttribute('open')
      );
    }

    cancelScheduledCreate() {
      if (this._createRaf) {
        cancelAnimationFrame(this._createRaf);
        this._createRaf = null;
      }
      if (this._layoutRaf) {
        cancelAnimationFrame(this._layoutRaf);
        this._layoutRaf = null;
      }
    }

    /**
     * Wait 2 frames so matchMedia CSS (thumb display / direction) is applied
     * before Swiper measures the thumbs track.
     */
    scheduleCreateSliders() {
      if (!this.slider || !this.isQuickViewReady()) return;

      this.cancelScheduledCreate();
      this._createRaf = requestAnimationFrame(() => {
        this._createRaf = requestAnimationFrame(() => {
          this._createRaf = null;
          if (!this.isConnected || this.sliderInstance) return;
          if (!this.shouldInitSwiper() || !this.isQuickViewReady()) return;
          this.refreshGalleryElements();
          if (!this.slider) return;
          this.createSliders();
          this.refreshSliderLayout();
        });
      });
    }

    refreshSliderLayout() {
      this._layoutRaf = requestAnimationFrame(() => {
        this._layoutRaf = null;
        if (!this.sliderInstance || this.sliderInstance.destroyed) return;

        try {
          // Desktop thumbs may mount after CSS switches from `none` → flex.
          if (!this.thumbnailsInstance && this.areThumbnailsVisible()) {
            this.destroySlidersOnly();
            this.refreshGalleryElements();
            this.createSliders();
          }

          this.thumbnailsInstance?.update?.();
          this.sliderInstance?.update?.();
          this.sliderInstance?.updateSize?.();
          this.sliderInstance?.updateSlides?.();
          this.syncThumbsToActiveSlide(0);
          this.bindGalleryArrows();
        } catch (_) {
          /* ignore */
        }
      });
    }

    connectedCallback() {
      this.section = this.closest('.shopify-section');
      this.updateLayoutMode();
      this.mediaQuery.addEventListener('change', this.onResize);
      // iPad Air: orientation / Safari chrome change size without always
      // flipping the 1025px breakpoint — keep swiper measurements fresh.
      window.addEventListener('resize', this.onViewportResize);
      window.visualViewport?.addEventListener('resize', this.onViewportResize);
      this.addEventListener('lightbox:open', (e) => this.openZoom(e.detail.index || 0));
      this.setupGallery();
    }

    setupGallery() {
      this.refreshGalleryElements();

      if (
        this.slider &&
        !window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        this.slider.style.setProperty(
          '--swiper-wrapper-transition-timing-function',
          'cubic-bezier(0.16, 1, 0.3, 1)'
        );
      }

      this.updateLayoutMode();

      if (this.slider) {
        this.handleSlider();
      }
      this.initPhotoSwipe();
    }

    disconnectedCallback() {
      this.mediaQuery.removeEventListener('change', this.onResize);
      window.removeEventListener('resize', this.onViewportResize);
      window.visualViewport?.removeEventListener('resize', this.onViewportResize);
      this.cancelScheduledCreate();
      this.destroyPhotoSwipe();
      this.destroySlider();
    }

    resetGridGalleryStyles() {
      const viewer = this.querySelector('.product-media-gallery-viewer');
      const list = this.querySelector('.product-media-list');
      const slides = this.querySelectorAll('.product-media-item');

      viewer?.style.removeProperty('overflow');
      list?.style.removeProperty('transform');
      list?.style.removeProperty('height');
      list?.style.removeProperty('transition-duration');
      slides.forEach((slide) => {
        slide.style.removeProperty('width');
        slide.style.removeProperty('height');
        slide.style.removeProperty('margin-right');
      });
    }

    handleSlider({ fromResize = false } = {}) {
      this.updateLayoutMode();
      this.refreshGalleryElements();

      const isDesktop = this.mediaQuery.matches;
      const breakpointChanged =
        this._lastIsDesktop != null && this._lastIsDesktop !== isDesktop;
      this._lastIsDesktop = isDesktop;

      // Desktop grid / stacked / columns — CSS only, no Swiper.
      if (this.isDesktopGridLayout()) {
        if (this.sliderInstance || this.thumbnailsInstance) {
          this.destroySlidersOnly();
        }
        this.resetGridGalleryStyles();
        this.teardownDesktopGallery();
        return;
      }

      this.teardownDesktopGallery();

      // Breakpoint / layout rebuild must always re-init (incl. thumbs direction).
      // Do not route resize through IntersectionObserver — that skips re-init when
      // the gallery is already on-screen.
      if (
        fromResize ||
        breakpointChanged ||
        this.sliderInstance ||
        this.thumbnailsInstance
      ) {
        this.destroySlidersOnly();
        this.refreshGalleryElements();
      }

      if (!this.slider) return;

      if (fromResize || breakpointChanged) {
        this.scheduleCreateSliders();
        return;
      }

      this.init();
    }

    init() {
      if (!this.slider) return;

      // Quick View mounts HTML while the drawer is still [hidden] — wait until open.
      if (!this.isQuickViewReady()) return;

      // Product gallery always eager (thumbs ↔ main sync); outside MainContent = QV too
      this.scheduleCreateSliders();
    }

    async createThumbnailsInstance() {
      if (!this.thumbnails || !this.areThumbnailsVisible()) return null;

      return createSlider(this.thumbnails, {
        preventClicks: false,
        preventClicksPropagation: false,
        bypassLcpGate: true,
      });
    }

    createSliders() {
      if (this.sliderInstance || !this.slider || !this.shouldInitSwiper()) return;

      // Desktop: Liquid `data-slider` only (no mobile JS overrides).
      if (this.shouldUseLiquidSlider()) {
        void this.createLiquidSliders();
        return;
      }

      void this.createMobileSliders();
    }

    async createMobileSliders() {
      if (this.sliderInstance || !this.slider || !this.shouldInitSwiper()) return;

      const paginationEl = this.querySelector(
        '[data-product-gallery-pagination][data-pagination-type="bullets"]',
      );

      const mainParams = {
        spaceBetween: 10,
        slidesPerView: 1,
        watchSlidesProgress: true,
        watchOverflow: false,
        allowTouchMove: true,
        simulateTouch: true,
        grabCursor: true,
        navigation: false,
        ...(paginationEl && {
          pagination: {
            el: paginationEl,
            clickable: true,
            type: 'bullets',
          },
        }),
      };

      this.thumbnailsInstance = await this.createThumbnailsInstance();

      if (this.thumbnailsInstance?.el) {
        mainParams.thumbs = {
          swiper: this.thumbnailsInstance,
          autoScrollOffset: 1,
          multipleActiveThumbs: false,
        };
      }

      this.sliderInstance = await createSlider(this.slider, {
        ...mainParams,
        ...smoothSwiperOverrides(mainParams),
        bypassLcpGate: true,
      });

      this.bindThumbsSync();
      this.bindGalleryArrows();
      this.bindThumbnailArrows();
      this.bindModelSwipeBridge();
      this.initPhotoSwipe();
    }

    /** Desktop Swiper — options come from Liquid `data-slider` / `data-thumbnails`. */
    async createLiquidSliders() {
      if (this.sliderInstance || !this.slider) return;

      this.thumbnailsInstance = await this.createThumbnailsInstance();

      const params = {
        fluidWidth: true,
        roundLengths: true,
        bypassLcpGate: true,
      };
      if (this.thumbnailsInstance?.el) {
        params.thumbs = {
          swiper: this.thumbnailsInstance,
          autoScrollOffset: 1,
          multipleActiveThumbs: false,
        };
      }

      this.sliderInstance = await createSlider(this.slider, params);

      this.bindThumbsSync();
      this.bindGalleryArrows();
      this.bindThumbnailArrows();
      this.bindModelSwipeBridge();
      this.initPhotoSwipe();
    }

    getMainSlides() {
      return [
        ...this.querySelectorAll(
          '.product-media-gallery-viewer .product-media-item:not([hidden])',
        ),
      ];
    }

    showDesktopSlide(index) {
      const slides = this.getMainSlides();
      if (!slides.length) return;

      const targetIndex = Math.min(Math.max(index, 0), slides.length - 1);
      slides.forEach((slide, i) => {
        const active = i === targetIndex;
        slide.classList.toggle('is-desktop-active', active);
        slide.toggleAttribute('data-selected-media', active);
      });

      this._desktopActiveIndex = targetIndex;
      this.syncDesktopThumbs(targetIndex);
      this._syncGalleryArrowState?.();
      this.pauseInactiveModelsDesktop(slides[targetIndex]);
    }

    syncDesktopThumbs(index) {
      const slides = this.getMainSlides();
      const mediaId = slides[index]?.dataset?.mediaId;
      const thumbs = [...this.querySelectorAll('[data-product-thumb]')];

      thumbs.forEach((thumb, i) => {
        const thumbSlide = thumb.closest('.swiper-slide') || thumb;
        const active = mediaId
          ? String(thumb.dataset.target) === String(mediaId)
          : i === index;
        thumbSlide.classList.toggle('swiper-slide-thumb-active', active);
      });

      const thumbsSwiper = this.thumbnailsInstance;
      if (thumbsSwiper && !thumbsSwiper.destroyed) {
        const activeThumbIndex = mediaId
          ? thumbs.findIndex((thumb) => String(thumb.dataset.target) === String(mediaId))
          : index;
        if (activeThumbIndex < 0) return;

        const maxIndex = Math.max((thumbsSwiper.slides?.length || 1) - 1, 0);
        const targetIndex = Math.min(Math.max(activeThumbIndex, 0), maxIndex);
        try {
          thumbsSwiper.slideTo(targetIndex, 300);
        } catch (_) {
          /* ignore */
        }
        return;
      }

      const activeThumb = mediaId
        ? thumbs.find((thumb) => String(thumb.dataset.target) === String(mediaId))
        : thumbs[index];
      activeThumb?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }

    pauseInactiveModels() {
      if (!this.sliderInstance || this.sliderInstance.destroyed) return;
      const activeSlide = this.sliderInstance.slides?.[this.sliderInstance.activeIndex];

      this.querySelectorAll('model-media').forEach((media) => {
        if (activeSlide?.contains(media)) return;
        try {
          media.pause?.();
          media.removeAttribute('playing');
          media.closeElement?.setAttribute('hidden', '');
        } catch (_) {
          /* ignore */
        }
      });
    }

    pauseInactiveModelsDesktop(activeSlide) {
      this.querySelectorAll('model-media').forEach((media) => {
        if (activeSlide?.contains(media)) return;
        try {
          media.pause?.();
          media.removeAttribute('playing');
          media.closeElement?.setAttribute('hidden', '');
        } catch (_) {
          /* ignore */
        }
      });
    }

    setupDesktopGallery() {
      if (!this.isDesktopStaticGallery()) return;

      this.cancelScheduledCreate();
      this._createRaf = requestAnimationFrame(() => {
        this._createRaf = requestAnimationFrame(() => {
          this._createRaf = null;
          if (!this.isConnected || !this.isDesktopStaticGallery()) return;

          this.refreshGalleryElements();

          if (this.thumbnails && !this.thumbnailsInstance) {
            this.thumbnailsInstance = this.createThumbnailsInstance();
          }

          const slides = this.getMainSlides();
          const initialIndex = Math.max(
            slides.findIndex((slide) => slide.hasAttribute('data-selected-media')),
            0,
          );

          this.showDesktopSlide(initialIndex);
          this.bindDesktopGallery();
          this.thumbnailsInstance?.update?.();
          this.initPhotoSwipe();
        });
      });
    }

    bindDesktopGallery() {
      this.unbindDesktopGallery();

      const thumbsRoot = this.querySelector('[data-product-gallery-thumbs]');
      this._onDesktopThumbClick = (event) => {
        const thumb = event.target.closest('[data-product-thumb]');
        if (!thumb || !thumbsRoot?.contains(thumb)) return;

        const mediaId = thumb.dataset.target;
        const slides = this.getMainSlides();

        if (mediaId) {
          const index = slides.findIndex(
            (slide) => String(slide.dataset.mediaId) === String(mediaId),
          );
          if (index >= 0) {
            this.showDesktopSlide(index);
            return;
          }
        }

        const thumbs = [...this.querySelectorAll('[data-product-thumb]')];
        const index = thumbs.indexOf(thumb);
        if (index >= 0) this.showDesktopSlide(index);
      };

      thumbsRoot?.addEventListener('click', this._onDesktopThumbClick);
      this.bindGalleryArrows();
      this.bindThumbnailArrows();
    }

    unbindDesktopGallery() {
      const thumbsRoot = this.querySelector('[data-product-gallery-thumbs]');
      if (this._onDesktopThumbClick) {
        thumbsRoot?.removeEventListener('click', this._onDesktopThumbClick);
      }
      this._onDesktopThumbClick = null;
    }

    teardownDesktopGallery() {
      this.cancelScheduledCreate();
      this.unbindDesktopGallery();
      this.unbindGalleryArrows();
      this.unbindThumbnailArrows();
      this._desktopActiveIndex = null;

      this.querySelectorAll('.product-media-item.is-desktop-active').forEach((slide) => {
        slide.classList.remove('is-desktop-active');
      });

      if (this.thumbnailsInstance && !this.sliderInstance) {
        this.thumbnailsInstance = destroySlider(this.thumbnailsInstance, {
          element: this.thumbnails,
        });
      }
    }

    /**
     * After 3D play, model-viewer covers the slide and eats pointer events
     * (paused state uses pointer-events:none). Horizontal drags hand off to Swiper.
     */
    bindModelSwipeBridge() {
      this.unbindModelSwipeBridge();
      if (!this.sliderInstance || this.sliderInstance.destroyed) return;

      this._modelSwipe = null;
      this._onModelGalleryPointerDown = (event) => {
        if (!this.sliderInstance || this.sliderInstance.destroyed) return;
        if (event.isPrimary === false) return;

        const viewer = event.target?.closest?.('model-viewer');
        if (!viewer || viewer.classList.contains('shopify-model-viewer-ui__disabled')) return;

        this._modelSwipe = {
          tracking: true,
          handedOff: false,
          startX: event.clientX,
          startY: event.clientY,
          pointerId: event.pointerId,
          viewer,
        };
      };

      this._onModelGalleryPointerMove = (event) => {
        const state = this._modelSwipe;
        if (!state?.tracking || state.handedOff) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        if (!this.sliderInstance || this.sliderInstance.destroyed) return;

        const dx = event.clientX - state.startX;
        const dy = event.clientY - state.startY;
        const absX = Math.abs(dx);
        const absY = Math.abs(dy);
        if (absX < 28 && absY < 28) return;

        // Mostly horizontal → gallery slide; otherwise leave orbit to model-viewer.
        if (absX <= absY * 1.15) return;

        state.handedOff = true;
        state.tracking = false;

        const viewer = state.viewer;
        if (viewer) viewer.style.pointerEvents = 'none';

        if (dx < 0) this.sliderInstance.slideNext();
        else this.sliderInstance.slidePrev();
        this.syncThumbsToActiveSlide();

        const restore = () => {
          if (viewer?.isConnected) viewer.style.pointerEvents = '';
        };
        this.sliderInstance.once?.('slideChangeTransitionEnd', restore);
        setTimeout(restore, 450);
      };

      this._onModelGalleryPointerUp = (event) => {
        const state = this._modelSwipe;
        if (!state) return;
        if (state.pointerId != null && event.pointerId !== state.pointerId) return;
        state.tracking = false;
        if (!state.handedOff) this._modelSwipe = null;
      };

      this._onModelSlideChangePause = () => {
        this.pauseInactiveModels();
      };

      this.addEventListener('pointerdown', this._onModelGalleryPointerDown, true);
      this.addEventListener('pointermove', this._onModelGalleryPointerMove, true);
      this.addEventListener('pointerup', this._onModelGalleryPointerUp, true);
      this.addEventListener('pointercancel', this._onModelGalleryPointerUp, true);
      this.sliderInstance.on('slideChange', this._onModelSlideChangePause);
    }

    unbindModelSwipeBridge() {
      if (this._onModelGalleryPointerDown) {
        this.removeEventListener('pointerdown', this._onModelGalleryPointerDown, true);
        this.removeEventListener('pointermove', this._onModelGalleryPointerMove, true);
        this.removeEventListener('pointerup', this._onModelGalleryPointerUp, true);
        this.removeEventListener('pointercancel', this._onModelGalleryPointerUp, true);
      }
      if (this.sliderInstance && this._onModelSlideChangePause) {
        this.sliderInstance.off('slideChange', this._onModelSlideChangePause);
      }
      this._onModelGalleryPointerDown = null;
      this._onModelGalleryPointerMove = null;
      this._onModelGalleryPointerUp = null;
      this._onModelSlideChangePause = null;
      this._modelSwipe = null;
    }

    getActiveSlideIndex() {
      if (this.sliderInstance && !this.sliderInstance.destroyed) {
        return this.sliderInstance.realIndex ?? this.sliderInstance.activeIndex ?? 0;
      }

      const slides = this.getMainSlides();
      const selectedIndex = slides.findIndex((slide) =>
        slide.hasAttribute('data-selected-media'),
      );
      if (selectedIndex >= 0) return selectedIndex;

      return this._desktopActiveIndex ?? 0;
    }

    syncThumbsToActiveSlide(speed) {
      const thumbs = this.thumbnailsInstance;
      if (!thumbs || thumbs.destroyed || !thumbs.el) return;
      const index = this.getActiveSlideIndex();
      const maxIndex = Math.max((thumbs.slides?.length || 1) - 1, 0);
      const targetIndex = Math.min(Math.max(index, 0), maxIndex);

      thumbs.slides?.forEach((slide, i) => {
        if (!slide) return;
        slide.classList.toggle('swiper-slide-thumb-active', i === targetIndex);
      });

      if (thumbs.activeIndex !== targetIndex) {
        const duration =
          typeof speed === 'number' ? speed : thumbs.params?.speed || 300;
        try {
          thumbs.slideTo(targetIndex, duration);
        } catch (_) {
          /* ignore mid-destroy races */
        }
      }
    }

    bindThumbsSync() {
      this.unbindThumbsSync();
      if (!this.sliderInstance || this.sliderInstance.destroyed) return;

      this._onMainSlideChange = () => {
        this.syncThumbsToActiveSlide();
      };

      // Native click — Swiper clickedIndex is unreliable on mobile touch.
      this._onThumbClick = (event) => {
        const slide = event.target?.closest?.('.swiper-slide');
        if (!slide || !this.thumbnails?.contains(slide)) return;

        const index = [...(this.thumbnailsInstance?.slides || [])].indexOf(slide);
        if (index < 0) return;

        if (this.sliderInstance && !this.sliderInstance.destroyed && this.sliderInstance.el) {
          try {
            this.sliderInstance.slideTo(index);
          } catch (_) {
            /* ignore */
          }
        }
        this.syncThumbsToActiveSlide(0);
      };

      this.sliderInstance.on('slideChange', this._onMainSlideChange);
      this.sliderInstance.on('slideChangeTransitionStart', this._onMainSlideChange);
      this.thumbnails?.addEventListener('click', this._onThumbClick);
      this.syncThumbsToActiveSlide(0);
    }

    unbindThumbsSync() {
      if (this.sliderInstance && this._onMainSlideChange) {
        this.sliderInstance.off('slideChange', this._onMainSlideChange);
        this.sliderInstance.off('slideChangeTransitionStart', this._onMainSlideChange);
      }
      if (this.thumbnails && this._onThumbClick) {
        this.thumbnails.removeEventListener('click', this._onThumbClick);
      }
      this._onMainSlideChange = null;
      this._onThumbClick = null;
    }

    initPhotoSwipe() {
      if (this.lightbox) return;
      if (!this.hasAttribute('data-enable-zoom')) return;

      this.galleryEl = this.querySelector('[data-product-photoswipe-gallery]');
      if (!this.galleryEl) return;

      this.lightbox = new PhotoSwipeLightbox({
        gallery: this.galleryEl,
        children: '[data-product-photoswipe-item]:not([hidden])',
        pswpModule: () => import('@lib/photoswipe-core'),
        showHideAnimationType: 'zoom',
        bgOpacity: 1,
        loop: true,
        arrowPrev: true,
        arrowNext: true,
        arrowKeys: true,
        pinchToClose: true,
        closeOnVerticalDrag: true,
      });

      this.lightbox.addFilter('domItemData', (itemData, element) => {
        if (itemData.src) return itemData;

        const img = element.querySelector('img');
        if (!img) return itemData;

        itemData.src = element.dataset.pswpSrc || img.currentSrc || img.src;
        itemData.width =
          parseInt(element.dataset.pswpWidth, 10) || img.naturalWidth || img.width || 1200;
        itemData.height =
          parseInt(element.dataset.pswpHeight, 10) || img.naturalHeight || img.height || 1200;
        itemData.w = itemData.width;
        itemData.h = itemData.height;
        itemData.msrc = img.currentSrc || img.src;
        itemData.alt = img.getAttribute('alt') || '';

        return itemData;
      });

      this.lightbox.addFilter('clickedIndex', (index, event) => {
        const item = event.target.closest('[data-product-photoswipe-item]:not([hidden])');
        if (!item) return index;

        const items = this.getPhotoSwipeItems();
        const itemIndex = items.indexOf(item);
        return itemIndex >= 0 ? itemIndex : index;
      });

      this.lightbox.on('beforeOpen', () => {
        document.documentElement.classList.add('photoswipe-open');
      });

      this.lightbox.on('change', () => {
        this.syncGalleryFromLightbox();
      });

      this.lightbox.on('close', () => {
        document.documentElement.classList.remove('photoswipe-open');
        this.syncGalleryFromLightbox();
      });

      this.lightbox.init();
    }

    syncGalleryFromLightbox() {
      const pswp = this.lightbox?.pswp;
      if (!pswp) return;

      const items = this.getPhotoSwipeItems();
      const item = items[pswp.currIndex];
      if (!item) return;

      if (this.sliderInstance && !this.sliderInstance.destroyed) {
        const slides = this.sliderInstance.slides || [];
        const slideIndex = [...slides].findIndex(
          (slide) => slide === item || slide.contains?.(item),
        );

        if (slideIndex < 0) return;

        if (slideIndex !== this.sliderInstance.activeIndex) {
          try {
            this.sliderInstance.slideTo(slideIndex, 0);
          } catch (_) {
            /* ignore */
          }
        }

        this.syncThumbsToActiveSlide(0);
        return;
      }

      if (this.isDesktopStaticGallery()) {
        const slideIndex = this.getMainSlides().findIndex(
          (slide) => slide === item || slide.contains?.(item),
        );
        if (slideIndex >= 0) this.showDesktopSlide(slideIndex);
        return;
      }

      if (window.lenis?.scrollTo) {
        window.lenis.scrollTo(item, { offset: 0 });
        return;
      }

      item.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
    }

    getPhotoSwipeItems() {
      if (!this.galleryEl) return [];

      return [
        ...this.galleryEl.querySelectorAll('[data-product-photoswipe-item]:not([hidden])'),
      ];
    }

    openZoom(index = 0) {
      if (!this.lightbox) this.initPhotoSwipe();
      if (!this.lightbox || !this.galleryEl) return;

      const items = this.getPhotoSwipeItems();
      if (!items.length) return;

      let openIndex = index;

      if (this.sliderInstance && !Number.isInteger(index)) {
        const activeSlide = this.sliderInstance.slides[this.sliderInstance.activeIndex];
        const activeItem = activeSlide?.querySelector('[data-product-photoswipe-item]:not([hidden])');
        if (activeItem) {
          openIndex = items.indexOf(activeItem);
        }
      } else if (this.isDesktopStaticGallery() && !Number.isInteger(index)) {
        const activeSlide = this.getMainSlides().find((slide) =>
          slide.hasAttribute('data-selected-media'),
        );
        const activeItem = activeSlide?.querySelector('[data-product-photoswipe-item]:not([hidden])');
        if (activeItem) {
          openIndex = items.indexOf(activeItem);
        }
      } else {
        const byPswpIndex = items.findIndex(
          (item) => parseInt(item.dataset.pswpIndex, 10) === index,
        );
        if (byPswpIndex >= 0) {
          openIndex = byPswpIndex;
        } else {
          openIndex = Math.min(Math.max(index, 0), items.length - 1);
        }
      }

      if (openIndex < 0) openIndex = 0;

      this.lightbox.loadAndOpen(openIndex, { gallery: this.galleryEl });
    }

    destroyPhotoSwipe() {
      this.lightbox?.destroy();
      this.lightbox = null;
      document.documentElement.classList.remove('photoswipe-open');
    }

    bindGalleryArrows() {
      this.unbindGalleryArrows();
      if (!this.mediaQuery.matches) return;

      const controls = this.querySelector('[data-product-gallery-controls]');
      if (!controls) return;

      const nextBtn = controls.querySelector('.swiper-button-next');
      const prevBtn = controls.querySelector('.swiper-button-prev');
      if (!nextBtn && !prevBtn) return;

      // Never leave lock/disabled attrs from a bad first paint (common when thumbs are hidden).
      [nextBtn, prevBtn].forEach((btn) => {
        btn?.classList.remove('swiper-button-lock');
        btn?.removeAttribute('disabled');
      });

      this._getMainSlideCount = () => {
        if (this.isDesktopStaticGallery()) {
          return this.getMainSlides().length;
        }

        const fromDom =
          this.slider?.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)')
            ?.length || 0;
        if (fromDom > 0) return fromDom;
        return this.sliderInstance?.slides?.length || 0;
      };

      this._syncGalleryArrowState = () => {
        const hasSwiper = this.sliderInstance && !this.sliderInstance.destroyed;
        if (!hasSwiper && !this.isDesktopStaticGallery()) return;

        const index = this.getActiveSlideIndex();
        const slideCount = this._getMainSlideCount();
        const last = Math.max(slideCount - 1, 0);
        const atStart = index <= 0;
        const atEnd = slideCount <= 1 || index >= last;

        prevBtn?.classList.toggle('swiper-button-disabled', atStart);
        nextBtn?.classList.toggle('swiper-button-disabled', atEnd);
        // Visual only — HTML disabled blocks clicks and was stranding both arrows.
        prevBtn?.removeAttribute('disabled');
        nextBtn?.removeAttribute('disabled');
        prevBtn?.classList.remove('swiper-button-lock');
        nextBtn?.classList.remove('swiper-button-lock');
      };

      this._onGalleryNext = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.sliderInstance && !this.sliderInstance.destroyed) {
          this.sliderInstance.slideNext();
          this.syncThumbsToActiveSlide();
        } else if (this.isDesktopStaticGallery()) {
          const last = Math.max(this.getMainSlides().length - 1, 0);
          this.showDesktopSlide(Math.min(this.getActiveSlideIndex() + 1, last));
        }
        this._syncGalleryArrowState?.();
      };
      this._onGalleryPrev = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.sliderInstance && !this.sliderInstance.destroyed) {
          this.sliderInstance.slidePrev();
          this.syncThumbsToActiveSlide();
        } else if (this.isDesktopStaticGallery()) {
          this.showDesktopSlide(Math.max(this.getActiveSlideIndex() - 1, 0));
        }
        this._syncGalleryArrowState?.();
      };

      nextBtn?.addEventListener('click', this._onGalleryNext);
      prevBtn?.addEventListener('click', this._onGalleryPrev);
      if (this.sliderInstance && !this.sliderInstance.destroyed) {
        this.sliderInstance.on('slideChange', this._syncGalleryArrowState);
        this.sliderInstance.on('slideChangeTransitionEnd', this._syncGalleryArrowState);
      }
      this._syncGalleryArrowState();
      requestAnimationFrame(() => this._syncGalleryArrowState?.());
    }

    unbindGalleryArrows() {
      const controls = this.querySelector('[data-product-gallery-controls]');
      if (!controls) return;
      const nextBtn = controls.querySelector('.swiper-button-next');
      const prevBtn = controls.querySelector('.swiper-button-prev');
      if (this._onGalleryNext) nextBtn?.removeEventListener('click', this._onGalleryNext);
      if (this._onGalleryPrev) prevBtn?.removeEventListener('click', this._onGalleryPrev);
      if (this._syncGalleryArrowState && this.sliderInstance?.off) {
        this.sliderInstance.off('slideChange', this._syncGalleryArrowState);
        this.sliderInstance.off('slideChangeTransitionEnd', this._syncGalleryArrowState);
      }
      this._onGalleryNext = null;
      this._onGalleryPrev = null;
      this._syncGalleryArrowState = null;
      this._getMainSlideCount = null;
    }

    bindThumbnailArrows() {
      this.unbindThumbnailArrows();
      const thumbsWrapper = this.querySelector('[data-product-gallery-thumbs]');

      if (!thumbsWrapper) return;

      const nextBtn = thumbsWrapper.querySelector('.swiper-button-next');
      const prevBtn = thumbsWrapper.querySelector('.swiper-button-prev');

      if (!nextBtn && !prevBtn) return;

      this._onThumbNext = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.sliderInstance && !this.sliderInstance.destroyed) {
          this.sliderInstance.slideNext();
          this.syncThumbsToActiveSlide();
          return;
        }
        if (this.isDesktopStaticGallery()) {
          const last = Math.max(this.getMainSlides().length - 1, 0);
          this.showDesktopSlide(Math.min(this.getActiveSlideIndex() + 1, last));
        }
      };
      this._onThumbPrev = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.sliderInstance && !this.sliderInstance.destroyed) {
          this.sliderInstance.slidePrev();
          this.syncThumbsToActiveSlide();
          return;
        }
        if (this.isDesktopStaticGallery()) {
          this.showDesktopSlide(Math.max(this.getActiveSlideIndex() - 1, 0));
        }
      };

      nextBtn?.addEventListener('click', this._onThumbNext);
      prevBtn?.addEventListener('click', this._onThumbPrev);
    }

    unbindThumbnailArrows() {
      const thumbsWrapper = this.querySelector('[data-product-gallery-thumbs]');
      if (!thumbsWrapper) return;
      const nextBtn = thumbsWrapper.querySelector('.swiper-button-next');
      const prevBtn = thumbsWrapper.querySelector('.swiper-button-prev');
      if (this._onThumbNext) nextBtn?.removeEventListener('click', this._onThumbNext);
      if (this._onThumbPrev) prevBtn?.removeEventListener('click', this._onThumbPrev);
      this._onThumbNext = null;
      this._onThumbPrev = null;
    }

    destroySlidersOnly() {
      this.cancelScheduledCreate();
      this.unbindModelSwipeBridge();
      this.unbindThumbsSync();

      // Unlink thumbs before destroy so slideTo/updateActiveIndex cannot read undefined.el.
      try {
        if (this.sliderInstance?.thumbs) {
          this.sliderInstance.thumbs.swiper = null;
        }
        if (this.sliderInstance?.params?.thumbs) {
          this.sliderInstance.params.thumbs.swiper = null;
        }
      } catch (_) {
        /* ignore */
      }

      // Main first, then thumbs.
      this.sliderInstance = destroySlider(this.sliderInstance, {
        element: this.slider,
      });
      this.thumbnailsInstance = destroySlider(this.thumbnailsInstance, {
        element: this.thumbnails,
      });
    }

    destroySlider() {
      this.destroyPhotoSwipe();
      this.teardownDesktopGallery();
      this.destroySlidersOnly();
    }
  }

  customElements.define('product-gallery', ProductGallery);
}
